import { useFetch } from "../../../hooks/api";
import { useMemo, useState } from "react";
import { useAppSelector } from "../../../redux/hooks";
import { useDebounce } from "@reactuses/core";
import { SearchInput } from "../../../components/fields/base/TextField";
import { Button, LinkButton } from "../../../components/core/basic/Button";
import { css } from "../../../../styled-system/css";
import { gridItem } from "../../../../styled-system/patterns";
import { Flex, Grid, GridItem } from "../../../../styled-system/jsx";
import { Tabs, TabList, Tab, TabPanel } from "../../../components/core/basic/Tabs";
import { FaTimes } from "react-icons/fa";
import useDefaultState from "../../../hooks/useDefaultState";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import { successToast, errorToast } from "../../../utils/toasts";
import { Modal } from "../../../components/core/basic/Modal";
import { useSimpleCan } from "../../../config/casl/casl";
import { useTranslation } from "react-i18next";

const noDataStyles = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "lightgrey/20",
  border: "1px dashed {colors.grey}",
  borderRadius: "5px",
  color: "darkgrey",
  fontStyle: "italic",
  fontWeight: "600",
  textAlign: "center",
  width: "100%",
});

const PlaceHolder = ({ children, height, width = "100%" }) => {
  return (
    <div className={noDataStyles} style={{ height, width }}>
      {children}
    </div>
  );
};

const scrollContainerStyles = css({
  overflowY: "auto",
  overflowX: "hidden",
});

const AudienceControl = ({ audiences, prefix, audience, onAdd, onRemove }) => {
  let label = audience?.audienceName;
  if (prefix) {
    label = label.replace(prefix + " - ", "");
  }
  const isSelected = !!audiences.some((a) => a.audienceId === audience.audienceId);
  return (
    <div className={css({ marginBottom: "xsmall" })}>
      <span
        onClick={() => {
          if (isSelected) {
            onRemove(audience);
          } else {
            onAdd(audience);
          }
        }}
        data-selected={isSelected}
        className={css({
          display: "inline-block",
          fontSize: "small",
          fontWeight: "600",
          paddingX: "small",
          paddingY: "xsmall",
          cursor: "pointer",
          color: "trellisPalette.2",
          border: "1px solid {colors.white}",
          borderRadius: "standard",
          maxWidth: "200px",
          wordBreak: "break-word",
          _hover: {
            borderColor: "trellisPalette.2",
          },
          "&[data-selected=true]": {
            borderColor: "trellisPalette.2",
          },
        })}
      >
        {label}
      </span>
    </div>
  );
};

const useAudienceList = ({ enabled = true, category, categoryPath, search }) => {
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const filter = {
    marketplace: marketPlace,
  };
  if (category) {
    filter.category = category;
  }
  if (categoryPath) {
    filter.category_path = categoryPath;
  }
  if (search) {
    filter.search = search;
  }
  const { data, isLoading } = useFetch(["dspAudiences", category, categoryPath, search], "/dsp/audiences", filter, {
    enabled,
    select: (data) => {
      return data?.data?.audiences;
    },
  });
  return { data, isLoading };
};

const AudienceCheckList = ({
  searchable = false,
  cols = 1,
  label,
  prefix,
  audiences,
  categoryPath,
  category,
  onAdd,
  onRemove,
}) => {
  const { t } = useTranslation(["dsp"]);
  const [_search, setSearch] = useState("");
  const search = useDebounce(_search, 300);
  const { data, isLoading } = useAudienceList({ enabled: true, categoryPath, search, category });
  const limit = 12 * cols;
  let limitedData =
    data?.sort((a, b) =>
      a.audienceName > b.audienceName ? 1
      : a.audienceName < b.audienceName ? -1
      : 0,
    ) ?? [];

  if (searchable) {
    limitedData = limitedData.slice(0, limit);
  }

  return (
    <Flex direction="column" gap="xsmall">
      {label && <h5>{label}</h5>}
      {searchable && (
        <SearchInput
          value={_search}
          onChange={(value) => setSearch(value)}
          placeholder={t(($) => $.dsp["Search..."])}
        />
      )}
      {isLoading && <div>{t(($) => $.dsp["Loading..."])}</div>}
      {data?.length === 0 && <div>{t(($) => $.dsp["No audiences found"])}</div>}
      <Flex direction="row" wrap="wrap" maxHeight={360}>
        {limitedData
          ?.sort((a, b) =>
            a.audienceName > b.audienceName ? 1
            : a.audienceName < b.audienceName ? -1
            : 0,
          )
          ?.map((audience) => (
            <div key={audience.audienceId} className={css({ flexBasis: "25%" })}>
              <AudienceControl
                key={audience.audienceId}
                prefix={prefix}
                audience={audience}
                audiences={audiences}
                onAdd={onAdd}
                onRemove={onRemove}
              />
            </div>
          ))}
      </Flex>
    </Flex>
  );
};

const DSPAudienceModal = ({ modalTitle, btnText, audiences, setAudiences }) => {
  const { t } = useTranslation(["dsp"]);
  const resolvedModalTitle = modalTitle ?? t(($) => $.dsp["Manage Audiences"]);
  const resolvedBtnText = btnText ?? t(($) => $.dsp["Manage"]);
  const [show, setShow] = useState(false);
  const [tab, setTab] = useState("inmarket");
  const userCanUpdateDSPEntity = useSimpleCan("update", "DSPEntity");

  const addAudience = (audience) => {
    // check for dups first
    if (audiences.some((a) => a.audienceId === audience.audienceId)) {
      return;
    }
    setAudiences([...audiences, audience]);
  };
  const removeAudience = (audience) => {
    setAudiences(audiences.filter((a) => a !== audience));
  };

  return (
    <>
      {userCanUpdateDSPEntity && <LinkButton onClick={() => setShow(true)}>{resolvedBtnText}</LinkButton>}
      {show && (
        <Modal
          title={resolvedModalTitle}
          isOpen={show}
          onOpenChange={(isOpen) => {
            setShow(isOpen);
          }}
          css={{
            width: "70vw",
            maxHeight: "95vh",
            paddingRight: "xlarge",
          }}
        >
          <Grid columns={4} gap="small" className={`px-5 ${css({ height: "700px" })}`}>
            <div
              className={gridItem({
                colSpan: 3,
                paddingRight: "20px",
                borderRight: "1px solid {colors.lightgrey}",
                display: "flex",
                flexDirection: "column",
                gap: "medium",
              })}
            >
              <Tabs selectedKey={tab} onSelectionChange={(k) => setTab(k)}>
                <TabList>
                  <Tab id="inmarket">{t(($) => $.dsp["In Market"])}</Tab>
                  <Tab id="demo">{t(($) => $.dsp["Demographics"])}</Tab>
                  <Tab id="interest">{t(($) => $.dsp["Interests"])}</Tab>
                  <Tab id="advertisers">{t(($) => $.dsp["Advertiser Audiences"])}</Tab>
                  <Tab id="custom">{t(($) => $.dsp["Custom-built"])}</Tab>
                </TabList>
                <TabPanel id="demo">
                  <div className={scrollContainerStyles} style={{ height: 580 }}>
                    <Flex direction="column" gap="medium">
                      <AudienceCheckList
                        label={t(($) => $.dsp["Gender"])}
                        categoryPath="Demographic,Gender"
                        prefix={"Demo"}
                        audiences={audiences}
                        onAdd={addAudience}
                        onRemove={removeAudience}
                      />
                      <AudienceCheckList
                        label={t(($) => $.dsp["Martial Status"])}
                        categoryPath="Demographic,Relationship"
                        prefix={"Demo"}
                        audiences={audiences}
                        onAdd={addAudience}
                        onRemove={removeAudience}
                      />
                      <AudienceCheckList
                        label={t(($) => $.dsp["Age"])}
                        prefix={"Demo"}
                        categoryPath="Demographic,Age"
                        audiences={audiences}
                        onAdd={addAudience}
                        onRemove={removeAudience}
                      />
                      <AudienceCheckList
                        label={t(($) => $.dsp["Income"])}
                        prefix={"Demo"}
                        categoryPath="Demographic,Income"
                        audiences={audiences}
                        onAdd={addAudience}
                        onRemove={removeAudience}
                      />
                      <AudienceCheckList
                        label={t(($) => $.dsp["Education"])}
                        prefix={"Demo"}
                        categoryPath="Demographic,Education"
                        audiences={audiences}
                        onAdd={addAudience}
                        onRemove={removeAudience}
                      />
                      <AudienceCheckList
                        label={t(($) => $.dsp["Occupation"])}
                        prefix={"Demo"}
                        categoryPath="Demographic,Occupation"
                        audiences={audiences}
                        onAdd={addAudience}
                        onRemove={removeAudience}
                      />
                      <AudienceCheckList
                        label={t(($) => $.dsp["Home Ownership"])}
                        prefix={"Demo"}
                        categoryPath="Demographic,Home Ownership"
                        audiences={audiences}
                        onAdd={addAudience}
                        onRemove={removeAudience}
                      />
                      <AudienceCheckList
                        label={t(($) => $.dsp["Household"])}
                        prefix={"Demo"}
                        categoryPath="Demographic,Household"
                        audiences={audiences}
                        onAdd={addAudience}
                        onRemove={removeAudience}
                      />
                    </Flex>
                  </div>
                </TabPanel>
                <TabPanel id="inmarket">
                  <div className={scrollContainerStyles} style={{ height: 580 }}>
                    <AudienceCheckList
                      searchable
                      cols={3}
                      prefix={"IM"}
                      category="In-market"
                      audiences={audiences}
                      onAdd={addAudience}
                      onRemove={removeAudience}
                    />
                  </div>
                </TabPanel>
                <TabPanel id="interest">
                  <div className={scrollContainerStyles} style={{ height: 580 }}>
                    <AudienceCheckList
                      searchable
                      cols={3}
                      prefix={"LS"}
                      category="Interest"
                      audiences={audiences}
                      onAdd={addAudience}
                      onRemove={removeAudience}
                    />
                  </div>
                </TabPanel>
                <TabPanel id="advertisers">
                  <div className={scrollContainerStyles} style={{ height: 580 }}>
                    <AudienceCheckList
                      searchable
                      cols={3}
                      category="Advertiser audiences"
                      audiences={audiences}
                      onAdd={addAudience}
                      onRemove={removeAudience}
                    />
                  </div>
                </TabPanel>
                <TabPanel id="custom">
                  <div className={scrollContainerStyles} style={{ height: 580 }}>
                    <AudienceCheckList
                      searchable
                      cols={3}
                      category="Custom-built"
                      audiences={audiences}
                      onAdd={addAudience}
                      onRemove={removeAudience}
                    />
                  </div>
                </TabPanel>
              </Tabs>
            </div>
            <div
              className={gridItem({
                paddingLeft: "10px",
              })}
            >
              <h4 style={{ marginTop: 0 }}>{t(($) => $.dsp["Selected Audiences"])}</h4>
              {audiences.map((audience) => (
                <AudienceControl
                  key={audience.audienceId}
                  audience={audience}
                  audiences={audiences}
                  onAdd={addAudience}
                  onRemove={removeAudience}
                />
              ))}
            </div>
          </Grid>
          <Flex justifyContent="end" gap="medium">
            <Button variant="secondary" onClick={() => setShow(false)}>
              {t(($) => $.dsp["Done"])}
            </Button>
          </Flex>
        </Modal>
      )}
    </>
  );
};

const GroupForm = ({ label, group, onChange, removeGroup }) => {
  const { t } = useTranslation(["dsp"]);
  const userCanUpdateDSPEntity = useSimpleCan("update", "DSPEntity");
  const { isExclusion, audiences } = group;

  return (
    <Grid
      columns={2}
      border="1px solid {colors.grey}"
      paddingX="medium"
      paddingY="medium"
      borderRadius="5px"
      width={320}
      gap="small"
    >
      <h4 className="mt-1">{label}</h4>
      {userCanUpdateDSPEntity && (
        <div className={css({ textAlign: "end" })}>
          <FaTimes
            className={css({
              cursor: "pointer",
              color: "grey",
              fontSize: "large",
            })}
            onClick={removeGroup}
          />
        </div>
      )}

      <h5>{isExclusion ? t(($) => $.dsp["Exclusions"]) : t(($) => $.dsp["Targets"])}</h5>
      <div className={css({ textAlign: "end" })}>
        <DSPAudienceModal
          audiences={audiences}
          setAudiences={(newAudiences) => onChange({ ...group, audiences: newAudiences })}
        />
      </div>
      <GridItem colSpan={2}>
        <div className={scrollContainerStyles} style={{ height: 100 }}>
          {audiences.length === 0 && (
            <div className={noDataStyles} style={{ height: 100 }}>
              {t(($) => $.dsp["No audiences"])}
            </div>
          )}
          {audiences.map((audience) => (
            <div key={audience.audienceId}>{audience.audienceName}</div>
          ))}
        </div>
      </GridItem>
    </Grid>
  );
};

const DSPAudienceGroupsForm = ({
  lineItemId,
  advertiserId,
  lineItemForm = false,
  onSave = () => {},
  onCancel = () => {},
}) => {
  const { t } = useTranslation(["dsp"]);
  const queryClient = useQueryClient();
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const userCanUpdateDSPEntity = useSimpleCan("update", "DSPEntity");

  const { data: targets } = useFetch(
    ["dspTargets", lineItemId],
    `/dsp/target/`,
    {
      line_item: lineItemId,
      marketplace: marketPlace,
      limit: 5000,
    },
    {
      enabled: !!lineItemId,
      select: (data) => data?.data?.results,
    },
  );
  const audienceTargets = useMemo(() => {
    if (!targets) {
      return [
        {
          isExclusion: false,
          inGroupOperator: "ANY",
          acrossGroupOperator: "ALL",
          audiences: [],
        },
      ];
    }

    const audienceTargets = targets.filter((t) => t.target_details?.audienceTarget);
    // group by Group Id
    const groups = audienceTargets.reduce((acc, target) => {
      const groupId = target.target_details.audienceTarget?.groupId;
      const audience = target.external_details;
      if (!acc[groupId]) {
        acc[groupId] = {
          inGroupOperator: target.target_details.audienceTarget.inGroupOperator,
          acrossGroupOperator: target.target_details.audienceTarget.acrossGroupOperator,
          audiences: [],
          isExclusion: !!target.negative,
        };
      }
      acc[groupId].audiences.push(audience);
      return acc;
    }, {});
    return (
      Object.entries(groups)
        .sort((a, b) => a[0] - b[0])

        // oxlint-disable-next-line no-unused-vars
        .map(([_, group]) => group)
    );
  }, [targets]);

  const [groups, setGroups, isDirty] = useDefaultState(audienceTargets);
  const removeGroup = (i) => {
    setGroups(groups.filter((_, j) => i !== j));
  };

  const hasExclusions = groups.some((g) => g.isExclusion);

  const formatAudienceGroupData = () => {
    const targets = [];
    groups.forEach((group, i) => {
      group.audiences.forEach((audience) => {
        targets.push({
          line_item: lineItemId,
          target_details: {
            audienceTarget: {
              groupId: (i + 1).toString(),
              audienceId: audience.audienceId,
              targetType: "AUDIENCE",
            },
          },
          negative: group.isExclusion,
        });
      });
    });
    return targets;
  };

  const updateGroups = useMutation({
    mutationFn: async () => {
      const targets = formatAudienceGroupData();

      await api.post("/dsp/target/", {
        target_type: "audienceTarget",
        advertiser_id: advertiserId,
        line_item: lineItemId,
        body: targets,
      });
    },

    onSuccess: () => {
      successToast({
        message: t(($) => $.dsp["Updated Line Item Audience Targets"]),
      });
      queryClient.invalidateQueries({
        queryKey: ["dspTargets"],
      });
      setGroups(undefined);
    },

    onError: (error) => {
      errorToast({
        message: t(($) => $.dsp["Failed updating Line Item Audience Targets"]),
        error,
      });
    },
  });

  return (
    <Flex
      direction="column"
      gap="large"
      className={css({
        width: "100%",
        xl: {
          width: "75%",
        },
      })}
    >
      <Flex direction="row" wrap="wrap" gap="large">
        {groups.map((group, i) => (
          <GroupForm
            key={i}
            group={group}
            label={t(($) => $.dsp["Group {n}"], { n: i + 1 })}
            onChange={(newGroup) => setGroups(groups.map((g, j) => (j === i ? newGroup : g)))}
            removeGroup={() => removeGroup(i)}
          />
        ))}
        {userCanUpdateDSPEntity && (
          <PlaceHolder width={320} height={240}>
            <Flex direction="column" gap="small">
              <h4>{t(($) => $.dsp["Add Group"])}</h4>
              <LinkButton onClick={() => setGroups([...groups, { audiences: [], isExclusion: false }])}>
                {t(($) => $.dsp["Targets"])}
              </LinkButton>
              {!hasExclusions && (
                <LinkButton onClick={() => setGroups([...groups, { audiences: [], isExclusion: true }])}>
                  {t(($) => $.dsp["Exclusions"])}
                </LinkButton>
              )}
            </Flex>
          </PlaceHolder>
        )}
      </Flex>
      {userCanUpdateDSPEntity && (
        <Flex direction="row" gap="medium" justifyContent={lineItemForm ? "center" : "end"}>
          <Button
            variant="primary"
            isPending={updateGroups.isPending}
            isDisabled={!isDirty || updateGroups.isPending}
            onClick={() =>
              lineItemForm && typeof onSave === "function" ? onSave(formatAudienceGroupData()) : updateGroups.mutate()
            }
          >
            {t(($) => $.dsp["Save"])}
          </Button>
          <Button
            variant="secondary"
            isDisabled={!isDirty}
            onClick={() => (lineItemForm && typeof onCancel === "function" ? onCancel() : setGroups(undefined))}
          >
            {t(($) => $.dsp["Cancel"])}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default DSPAudienceGroupsForm;
